import type { UserNode } from '@/modules/users'
import type { OnlyRequired } from '@/types/util'
import { PERMISSIONS } from '@/utils/permissions'
import { useAdmin } from './useAdmin'
import { useUser } from './useUser'

export function usePermissions() {
  const me = useUser()
  const admin = useAdmin()

  function hasPermissions(
    permissions: string | string[],
    allowAdmin: boolean = false
  ): boolean {
    return (
      me.isSuperuser ||
      (allowAdmin && admin) ||
      ([] as string[])
        .concat(permissions)
        .every(perm => me.allPermissions.includes(perm))
    )
  }

  function hasPermissionsOrMe(
    user: Pick<UserNode, 'id'> | null,
    ...permissions: string[]
  ): boolean {
    return !!user && (hasPermissions(permissions) || user.id === me.id)
  }

  function hasPermissionsAndMe(
    user: Pick<UserNode, 'id'> | null,
    ...permissions: string[]
  ): boolean {
    return !!user && hasPermissions(permissions) && user.id === me.id
  }

  return {
    permissions: me.allPermissions,
    hasPermissions,
    hasPermissionsOrMe,
    hasPermissionsAndMe,
  }
}

export function usePermissionsOrMe(
  user: Pick<UserNode, 'id'>,
  allowAdmin = false
) {
  const admin = useAdmin()
  const { hasPermissionsOrMe } = usePermissions()

  return (...perms: string[]) =>
    (allowAdmin && admin) || hasPermissionsOrMe(user, ...perms)
}

export function useProfileAccess(user: OnlyRequired<UserNode, 'id'>) {
  const admin = useAdmin()
  const { hasPermissionsOrMe } = usePermissions()

  return admin || hasPermissionsOrMe(user, PERMISSIONS.users.change.user)
}
